import {
  IconAnalytics,
  IconBell,
  IconBinocularsGray,
  IconChainLink,
  IconChartBar,
  IconChartBarGray,
  IconChartRadius,
  IconClockGray,
  IconCloudCheckGray,
  IconDashboard,
  IconLayers,
  IconLocation,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconSecurity,
  IconShareGray,
  IconTagGray,
  IconTvocGray,
  IconWarning,
  IconZoneGray,
} from '@assets/images/icon';
import { rtlsSystem } from '@assets/images/solution';
import mainVideo from '@src/assets/images/solution/constructionSafety/video/rtlsVideo.mp4';

import image1 from '@src/assets/images/solution/publicSafety/img/1.avif';
import function1 from '@src/assets/images/solution/publicSafety/img/function1.avif';
import function2 from '@src/assets/images/solution/publicSafety/img/function2.avif';
import function3 from '@src/assets/images/solution/publicSafety/img/function3.avif';
import function4 from '@src/assets/images/solution/publicSafety/img/function4.avif';
import image3 from '@src/assets/images/solution/publicSafety/img/3.avif';
import image4 from '@src/assets/images/solution/publicSafety/img/4.avif';
import image5 from '@src/assets/images/solution/publicSafety/img/5.avif';
import image6 from '@src/assets/images/solution/publicSafety/img/6.avif';
import image7 from '@src/assets/images/solution/publicSafety/img/7.avif';
import image8 from '@src/assets/images/solution/publicSafety/img/8.avif';
import image9 from '@src/assets/images/solution/publicSafety/img/9.avif';
import image10 from '@src/assets/images/solution/publicSafety/img/10.avif';

import customer1 from '@src/assets/images/solution/publicSafety/img/customer1.png';
import { publicSafety1, publicSafety2 } from '@assets/images/solution/recommendation';
import recommendation from '../common/recommendation';

export default {
  type: 'Urban Safety Solution',

  recommend: {
    title: '',
    desc: 'ORBRO OS is a powerful platform for real‑time location tracking and smart monitoring.<br/>It seamlessly integrates BLE, UWB, and AI‑based video analytics to enhance safety, efficiency, and scalability in public environments.',
    labels: {
      desktop: [
        { img: publicSafety1, data: recommendation.loraGateway },
        { img: publicSafety2, data: recommendation.safetyBell },
      ],
      mobile: [{ data: recommendation.safetyBell }, { data: recommendation.loraGateway }, { data: recommendation.server }],
    },
  },

  banner: {
    title: 'Protect the entire city in real time, with one button',
    desc: 'Deploy public safety bells to build city-wide infrastructure and protect citizens instantly.',
  },

  summary: {
    title: 'Build a monitoring system that manages the entire city at a glance.',
    desc: '',
    function: {
      card: [
        {
          image: function1,
          title: 'Dashboard for real-time operational metrics',
          desc: 'Analyze key indicators like reports, requests, usage rates, and regional demographics in real time. Designed to give situational clarity and support city-scale decision-making.',
        },
        {
          image: function2,
          title: 'Demand trends by request type',
          desc: 'Track requests from households, stores, and facilities by date. Understand peak demand periods to optimize planning and installation strategy.',
        },
        {
          image: function3,
          title: 'Regional data for spatial strategy',
          desc: 'Break down metrics like number of bells requested, registrations, and incidents across areas. Compare usage and incident rates to identify high-need zones.',
        },
        {
          image: function4,
          title: 'Track process from order to installation',
          desc: 'Monitor every step—from request to delivery, receipt, and installation—within the system. Enables proactive oversight and reduces omissions.',
        },
      ],
    },
  },

  numOfCard: {
    title: 'Operational metrics proven in real-world use',
    desc: 'Numbers that reflect how the system works—from requests to installs and alerts.',
    card: [
      {
        tag: '21,480 users',
        title: 'Total requests',
        desc: 'Number of safety bell requests since launch',
      },
      {
        tag: '96.7%',
        title: 'Registration completion rate',
        desc: 'Percent who fully registered their device after receiving it',
      },
      {
        tag: '62%',
        title: 'Periodic test button usage',
        desc: 'Played at least once per quarter',
      },
    ],
  },

  func: [
    {
      theme: 'Build a compact yet smart emergency device.',
      title: 'Immediate alert with one button press',
      desc: 'Pressing the button auto‑calls emergency services (e.g., 112) and sends SMS alerts to caregivers or contacts. Useful even without a smartphone—fast and reliable in emergencies.',
      sub1: 'High reliability',
      icon1: IconRulerGray,
      sub2: 'Real-time status',
      icon2: IconClockGray,
      image: image3,
    },
    {
      title: 'Mountable on walls, pillars, doors',
      desc: 'Designed for quick installation on various structures without power or complex work. Indoor or outdoor settings—easily mounted wherever needed.',
      sub2: 'Real-time location tracking',
      icon1: IconBinocularsGray,
      sub1: 'Fast data sync',
      icon2: IconTvocGray,
      image: image4,
    },
    {
      title: 'Self-test button for ongoing monitoring',
      desc: 'Built-in test button allows routine check-ups and confirms device operation—empowering both users and administrators.',
      sub1: 'Real-time location tracking',
      icon1: IconBinocularsGray,
      sub2: 'High reliability',
      icon2: IconRulerGray,
      image: image5,
    },
    {
      title: 'Ultra-low power for 10+ years operation',
      desc: 'Energy-efficient design enables long-term use without replacing or charging batteries, minimizing maintenance.',
      sub1: 'Flexible zone setup',
      icon1: IconZoneGray,
      sub2: 'Multi-zone support',
      icon2: IconTagGray,
      image: image6,
    },
    {
      theme: 'Streamline management and response with the companion app.',
      title: 'Real-time request tracking and cancellation',
      desc: 'App shows when alerts are received and allows cancellation within 2 minutes. Helps prevent misuse and ensures responsive control.',
      sub1: 'Flexible zone setup',
      icon1: IconZoneGray,
      sub2: 'Multi-zone support',
      icon2: IconTagGray,
      image: image7,
    },
    {
      title: 'Customize alert recipients',
      desc: 'Alerts can go to 112, caregivers, or buddies simultaneously. One alert—multiple recipients—for public and personal safety.',
      sub1: 'Flexible zone setup',
      icon1: IconZoneGray,
      sub2: 'Multi-zone support',
      icon2: IconTagGray,
      image: image8,
    },
    {
      title: 'Unified notification history',
      desc: 'The app logs all activities: triggered alerts, cancellations, device tests, low battery, replacement requests. Makes follow-up actions—like maintenance—easier to manage.',
      sub1: 'Flexible zone setup',
      icon1: IconZoneGray,
      sub2: 'Multi-zone support',
      icon2: IconTagGray,
      image: image9,
    },
    {
      title: 'Remote firmware updates',
      desc: 'Administrators can push firmware updates via the app—no additional hardware needed. Keeps devices up-to-date effortlessly.',
      sub1: 'Flexible zone setup',
      icon1: IconZoneGray,
      sub2: 'Multi-zone support',
      icon2: IconTagGray,
      image: image10,
    },
  ],

  customer: {
    title: 'Why many clients choose ORBRO—here’s their story.',
    card: [
      {
        logo: customer1,
        desc: '“After installation, seeing where, who, and how devices are used in real time is invaluable. It’s not just distribution—it helps us decide which areas to fortify next.”',
        name: 'Jung O‑yong, Director<br/>Gyeonggi Provincial Government',
      },
    ],
  },

  subfunc: {
    title: 'Feature Summary',
    desc: 'Ensuring worker safety improves efficiency and reduces losses. ORBRO provides tailored features to manage risks—whether near dangerous zones or during equipment use, the system responds appropriately.',
    data: [
      {
        icon: IconLocation,
        title: 'Emergency 112 call linkage',
        desc: 'One button press instantly dials 112 and sends an SMS to caregivers.',
      },
      {
        icon: IconLocation,
        title: 'Multi-recipient notifications',
        desc: 'Configure alerts for 112, caregivers, buddies, etc.',
      },
      {
        icon: IconAnalytics,
        title: 'Alert cancellation function',
        desc: 'Prevents misuse by allowing cancellation within 2 minutes via app.',
      },
      {
        icon: IconSearch,
        title: 'Remote firmware updates',
        desc: 'Admin can update firmware remotely via app.',
      },
      {
        icon: IconChartBar,
        title: 'Real-time battery monitoring',
        desc: 'App displays battery status and power levels live.',
      },
      {
        icon: IconLayers,
        title: 'Long-life low-power design',
        desc: 'Designed to run over a decade without battery change.',
      },
      {
        icon: IconSearch,
        title: 'On-site test button',
        desc: 'Manually verifies device function after installation.',
      },
      {
        icon: IconLayers,
        title: 'Fixed mounting installation',
        desc: 'Secures device firmly to walls or pillars.',
      },
      {
        icon: IconAnalytics,
        title: 'Data-driven operation strategy',
        desc: 'Use operational data to inform system improvements and planning.',
      },
      {
        icon: IconChartBar,
        title: 'Time-and-location analytics',
        desc: 'Provides analytics on usage by area, type, and time.',
      },
    ],
  },

  system: {
    title: 'System Configuration',
    img: rtlsSystem,
  },

  productTitle: 'Product Lineup',
  product: [{ tag: 'workerPackage' }, { tag: 'assetPackage' }],

  contents: [{ data: 'constructionAsset' }, { data: 'constructionEquipment' }, { data: 'blogRTLS' }, { data: 'blogMedical' }],
};
